import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useApi } from "../../containers/ApiContainer";
import { scrollSx } from "../../utils";

type Props = {};

export const AuthorizedAccounts: React.FC<Props> = () => {
  const [testEmail, setTestEmail] = React.useState<string>("");
  const [testResult, setTestResult] = React.useState<boolean>();
  const [emailOrDomain, setEmailOrDomain] = React.useState<string>("");
  const [authorizedAccounts, setAuthorizedAccounts] = React.useState<
    { email_or_domain: string }[]
  >([]);

  // Password whitelist state
  const [whitelistEmail, setWhitelistEmail] = React.useState<string>("");
  const [whitelistTestEmail, setWhitelistTestEmail] = React.useState<string>("");
  const [whitelistTestResult, setWhitelistTestResult] = React.useState<boolean>();
  const [passwordWhitelist, setPasswordWhitelist] = React.useState<
    { email: string; reason?: string; created_at: string }[]
  >([]);

  const { postgrest } = useApi();

  const fetchAuthorizedAccounts = async () => {
    const result = await postgrest.from("authorized_account").select("*");
    return result.data;
  };

  const fetchPasswordWhitelist = async () => {
    const result = await postgrest.from("password_auth_whitelist").select("*").order("created_at", { ascending: false });
    return result.data;
  };

  React.useEffect(() => {
    fetchAuthorizedAccounts().then(setAuthorizedAccounts);
    fetchPasswordWhitelist().then(setPasswordWhitelist);
  }, []);

  return (
    <Stack direction="column" spacing={1} sx={{ width: 400 }}>
      <List
        subheader={
          <>
            <ListSubheader component="div" id="nested-list-subheader">
              Valid Emails/Domains
            </ListSubheader>
            <Divider />
          </>
        }
      >
        <Box
          sx={{
            maxHeight: 500,
            overflow: "auto",
            ...scrollSx,
          }}
        >
          {authorizedAccounts.map((authorizedAccount) => (
            <ListItem
              key={authorizedAccount.email_or_domain}
              secondaryAction={
                <IconButton
                  onClick={async () => {
                    await postgrest
                      .from("authorized_account")
                      .delete()
                      .eq("email_or_domain", authorizedAccount.email_or_domain);
                    fetchAuthorizedAccounts().then(setAuthorizedAccounts);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={authorizedAccount.email_or_domain} />
            </ListItem>
          ))}
        </Box>
      </List>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          placeholder="New Email/Domain"
          size="small"
          onChange={(e) => setEmailOrDomain(e.target.value)}
          value={emailOrDomain}
        />
        <Button
          variant="contained"
          size="small"
          onClick={async () => {
            // don't let someone input a very liberal regex
            if (emailOrDomain.length < 3) {
              return;
            }
            await postgrest
              .from("authorized_account")
              .insert({ email_or_domain: emailOrDomain });
            fetchAuthorizedAccounts().then(setAuthorizedAccounts);
            setEmailOrDomain("");
          }}
        >
          Add
        </Button>
      </Stack>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Email to test"
            onChange={(e) => {
              setTestResult(null);
              setTestEmail(e.target.value);
            }}
            value={testEmail}
          />
          <Button
            variant="contained"
            size="small"
            onClick={async () => {
              setTestResult(null);
              const result = await postgrest.rpc("is_authorized", {
                email: testEmail,
              });
              setTestResult(result.data as unknown as boolean);
            }}
          >
            Test
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          {testResult === true && <CheckIcon sx={{ color: "green" }} />}
          {testResult === false && <CloseIcon sx={{ color: "red" }} />}
          <Typography>
            {testResult === true &&
              "Passed. This email will be allowed access."}
            {testResult === false &&
              "Failed. This email will not be allowed access."}
          </Typography>
        </Stack>
      </Stack>

      {/* Password Authentication Whitelist Section */}
      <Stack direction="column" spacing={1} sx={{ width: 400, mt: 3 }}>
        <List
          subheader={
            <>
              <ListSubheader component="div" id="password-whitelist-subheader">
                Password Authentication Whitelist
              </ListSubheader>
              <Divider />
            </>
          }
        >
          <Box
            sx={{
              maxHeight: 500,
              overflow: "auto",
              ...scrollSx,
            }}
          >
            {passwordWhitelist.map((item) => (
              <ListItem
                key={item.email}
                secondaryAction={
                  <IconButton
                    onClick={async () => {
                      await postgrest
                        .from("password_auth_whitelist")
                        .delete()
                        .eq("email", item.email);
                      fetchPasswordWhitelist().then(setPasswordWhitelist);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={item.email}
                  secondary={item.reason}
                />
              </ListItem>
            ))}
          </Box>
        </List>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            placeholder="New Email"
            size="small"
            onChange={(e) => setWhitelistEmail(e.target.value)}
            value={whitelistEmail}
          />
          <Button
            variant="contained"
            size="small"
            onClick={async () => {
              if (whitelistEmail.length < 3) {
                return;
              }
              await postgrest
                .from("password_auth_whitelist")
                .insert({
                  email: whitelistEmail,
                  reason: "Added via admin UI"
                });
              fetchPasswordWhitelist().then(setPasswordWhitelist);
              setWhitelistEmail("");
            }}
          >
            Add
          </Button>
        </Stack>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Email to test"
              onChange={(e) => {
                setWhitelistTestResult(null);
                setWhitelistTestEmail(e.target.value);
              }}
              value={whitelistTestEmail}
            />
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                setWhitelistTestResult(null);
                const result = await postgrest.rpc("is_password_auth_whitelisted", {
                  p_email: whitelistTestEmail,
                });
                setWhitelistTestResult(result.data as unknown as boolean);
              }}
            >
              Test
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            {whitelistTestResult === true && <CheckIcon sx={{ color: "green" }} />}
            {whitelistTestResult === false && <CloseIcon sx={{ color: "red" }} />}
            <Typography>
              {whitelistTestResult === true &&
                "Passed. This email can use password authentication."}
              {whitelistTestResult === false &&
                "Failed. This email must use Microsoft/Google login."}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
